import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { requestOtp } from '../../../services/apiServices';
import EmailInput from '../../Login/EmailInput';
import LogoBlue from '../../../assets/images/Logo-Blue.png';
import Logo from '../../../components/common/Logo';
import ErrorMessage from '../../../components/common/ErrorMessage';
import { PrimaryButton } from '../../../components/common/ButtonsV2';
import { useEmailValidation } from '../../../hooks';
import { ScreenHeader } from '../components/ScreenHeader';
import { BackToLoginButton } from '../components/BackToLoginButton';
import { SCREENS } from '../constants';

/**
 * Forgot Password Screen Component
 * First step in the password reset flow - user enters email to receive OTP
 */
const ForgotPasswordScreen = ({ onEmailSubmit, email, onNavigate }) => {
  const [isRequesting, setIsRequesting] = React.useState(false);
  const navigate = useNavigate();
  const emailInputRef = useRef(null);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const error = useSelector((state) => state.auth.error) || '';
  const emailErrorRef = useRef(null);

  const { emailError, setEmailError, validateEmail, handleEmailBlur, isEmailValid } =
    useEmailValidation();

  useEffect(() => {
    if (isAuthenticated) navigate('/summary');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    onEmailSubmit(value);
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRequesting) return;

    const validationResult = validateEmail(email);

    if (validationResult) {
      setEmailError(validationResult);
      emailErrorRef.current?.focus();
      return;
    }

    try {
      setIsRequesting(true);
      await requestOtp(email);
      onNavigate(SCREENS.VERIFY_CODE);
    } catch (error) {
      console.error('Request OTP error:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const onBackToLogin = () => {
    navigate('/');
  };

  const isValid = isEmailValid(email);

  return (
    <div className="flex flex-col items-center justify-center p-3 sm:p-4 md:p-6">
      <div className="w-full max-w-xs mx-auto">
        <Logo
          URL={LogoBlue}
          name="Janway Logo"
          variant="small"
          clickable={false}
          className="mb-4 mx-auto"
        />
      </div>

      <div className="w-full max-w-[450px]">
        <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
          <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
            <ScreenHeader
              icon="email"
              title="Forgot Password?"
              description="Enter your email and we'll send you a 6-digit verification code to reset your password."
            />
            <div className="w-full max-w-[450px]">
              <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
                <EmailInput
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => handleEmailBlur(email)}
                  error={emailError}
                  ref={emailInputRef}
                  errorRef={emailErrorRef}
                />

                <div className="space-y-4 pt-4">
                  <PrimaryButton
                    type="submit"
                    onClick={handleSubmit}
                    disabled={!isValid || isRequesting}
                    fullWidth={true}
                  >
                    {isRequesting ? 'Sending...' : 'Send Verification Code'}
                  </PrimaryButton>
                </div>

                <ErrorMessage message={error} className="text-center mt-4 !static w-full" />
              </form>
            </div>

            <BackToLoginButton onClick={onBackToLogin} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
